import {
    Combobox,
    ComboboxCollection,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList
} from "@solomon-labs/design/components/ui";
import type { ReactElement } from "react";
import { useMemo } from "react";

import { US_STATES } from "@/data/usStates";

/**
 * Searchable US state / territory picker, shown only once a jurisdiction
 * field has resolved to `US`.
 *
 * Deliberately the same shape as `CountrySelect` — same Combobox parts, same
 * `value` / `onChange(code | null)` contract, same case-insensitive match on
 * the way in — because the two render as one question split across two
 * controls and should not behave differently.
 *
 * Reports the USPS two-letter code; the caller folds it into `US-XX`.
 */
interface StateItem {
    value: string;
    label: string;
}

const ITEMS: readonly StateItem[] = US_STATES.map((s) => ({
    value: s.code,
    label: s.name
}));

export function UsStateSelect({
    value,
    onChange,
    disabled = false,
    error = false,
    id
}: {
    value: string | null;
    onChange: (code: string | null) => void;
    disabled?: boolean;
    /** Renders the error rail; the message itself is the caller's. */
    error?: boolean;
    id?: string;
}): ReactElement {
    const selected = useMemo(
        () => ITEMS.find((item) => item.value === value?.toUpperCase()) ?? null,
        [value]
    );

    return (
        <Combobox
            items={ITEMS}
            value={selected}
            onValueChange={(item: StateItem | null) =>
                onChange(item ? item.value : null)
            }
            disabled={disabled}
        >
            <ComboboxInput
                id={id}
                placeholder="Search states"
                disabled={disabled}
                /* `state`, not a raw `aria-invalid` — see the note in
                   CountrySelect: a nested input cannot draw its own rail. */
                state={error ? "error" : undefined}
            />
            <ComboboxContent>
                <ComboboxEmpty>No matching state.</ComboboxEmpty>
                <ComboboxList>
                    <ComboboxCollection>
                        {(item: StateItem) => (
                            <ComboboxItem key={item.value} value={item}>
                                {item.label}
                            </ComboboxItem>
                        )}
                    </ComboboxCollection>
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}
